/**
 * useArTableRowCount
 *
 * The ArTable footer's "N rows": the server aggregate, re-counted whenever the
 * rows collection gains or loses a document. Consumer: `ArTableBody`, which is
 * below `StoreProvider` and so holds the rows `CollectionState` this must watch.
 * It lived in `useArTablePagination` before, above `StoreProvider`, where it
 * could only look the store up in the singleton `registry` — and `StoreProvider`
 * registers nothing, so the re-count never ran for a row that arrived from the
 * server: another user's edit, a workflow, an import, a derive pass (SYD-2451).
 *
 * It subscribes to `docIds` alone, so a cell edit spends no aggregation query.
 * `docIds` is emptied in place, never reassigned, so the subscription survives a
 * reconnect and sees the collection come back. Local adds bump `totalRows`
 * optimistically in `useArTableActions`; the listener echo re-counts and confirms.
 */

import { useCallback, useEffect } from 'react';
import { collection, doc, getDoc, query, getCountFromServer } from 'firebase/firestore';
import { subscribe } from 'valtio';
import { firestore } from '@/services/startFirestore';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import type { ArRowData } from '#types/System.Artifacts.types';
import type { CollectionState } from '@/store/Store.types';
import type { ArTableUIState } from '../state/createArTableState';

type UseArTableRowCountParams = {
  uiState: ArTableUIState;
  rowsPath: string;
  /** The rows store's non-reactive state; `docIds` is the only leaf read. */
  rowsState: CollectionState<ArRowData>;
};

function useArTableRowCount({
  uiState,
  rowsPath,
  rowsState,
}: UseArTableRowCountParams): void {
  const runCount = useCallback(async (): Promise<void> => {
    try {
      const colRef = collection(firestore, rowsPath);
      const [countSnap, aggDoc] = await Promise.all([
        getCountFromServer(query(colRef)),
        getDoc(doc(firestore, rowsPath, AR_TABLE_AGGREGATIONS_ROW_ID)),
      ]);
      const rawCount = countSnap.data().count;
      const aggAdjustment = aggDoc.exists() ? 1 : 0;
      const count = Math.max(0, rawCount - aggAdjustment);
      uiState.pagination.totalRows = count;
      uiState.pagination.totalPages = Math.ceil(count / uiState.pagination.pageSize);
    } catch (error) {
      console.error('ArTable pagination count failed:', error);
    }
  }, [rowsPath, uiState]);

  /**
   * Subscribing to an external store — the rows collection's own membership.
   * The last length seen is effect-local because it is a property of this
   * subscription, not of the table: a fresh path starts from what it finds.
   */
  useEffect(() => {
    void runCount();
    const { docIds } = rowsState;
    let lastLength = docIds.length;
    return subscribe(docIds, () => {
      if (docIds.length === lastLength) return;
      lastLength = docIds.length;
      void runCount();
    });
  }, [runCount, rowsState]);
}

export { useArTableRowCount };
export type { UseArTableRowCountParams };
